import { useMemo, useState, type ChangeEvent } from 'react';
import { AlertCircle, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button, Input } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui';
import { Modal } from '../../components/ui/modal';
import { pageStyles } from '../../lib/styles';
import {
  useCreateServiceType,
  useServiceTypes,
  useUpdateServiceType,
  useDeleteServiceType,
} from '../../hooks/useServiceTypes';
import type { CreateServiceTypeInput, ServiceType, BayType } from '../../services/serviceTypesApi';
import { BAY_TYPE_LABELS } from '../../services/serviceTypesApi';
import { BAY_TYPES } from '../../services/statusTransitionService';
import { useToast } from '../../contexts/ToastContext';

function normalizeNumber(value: string, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

const emptyDraft: CreateServiceTypeInput = {
  name: '',
  nameEn: '',
  description: '',
  bayType: 'mechanical',
  slotsPerHour: 1,
  maintenanceIntervalDays: null,
  estimatedDurationMins: 60,
  isActive: true,
  sortOrder: 0,
};

export function ServiceTypesPage() {
  const { success: showSuccess, error: showError } = useToast();

  const [includeInactive, setIncludeInactive] = useState(true);
  const { data, isLoading, isError, error, refetch } = useServiceTypes(includeInactive);

  const createMutation = useCreateServiceType();
  const updateMutation = useUpdateServiceType();
  const deleteMutation = useDeleteServiceType();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceType | null>(null);
  const [draft, setDraft] = useState<CreateServiceTypeInput>(emptyDraft);
  const [deleteTarget, setDeleteTarget] = useState<ServiceType | null>(null);

  const sorted = useMemo(() => {
    const types = data || [];
    return [...types].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name);
    });
  }, [data]);

  const openCreate = () => {
    setEditing(null);
    setDraft({ ...emptyDraft });
    setIsModalOpen(true);
  };

  const openEdit = (st: ServiceType) => {
    setEditing(st);
    setDraft({
      name: st.name,
      nameEn: st.nameEn || '',
      description: st.description || '',
      bayType: st.bayType || 'mechanical',
      slotsPerHour: st.slotsPerHour,
      maintenanceIntervalDays: st.maintenanceIntervalDays,
      estimatedDurationMins: st.estimatedDurationMins,
      isActive: st.isActive,
      sortOrder: st.sortOrder,
    });
    setIsModalOpen(true);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    if (!draft.name.trim()) {
      showError('El nombre es obligatorio');
      return;
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data: draft });
        showSuccess('Servicio actualizado');
      } else {
        await createMutation.mutateAsync(draft);
        showSuccess('Servicio creado');
      }
      setIsModalOpen(false);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const toggleActive = async (st: ServiceType) => {
    try {
      await updateMutation.mutateAsync({ id: st.id, data: { isActive: !st.isActive } });
      showSuccess(!st.isActive ? 'Servicio activado' : 'Servicio desactivado');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Error al actualizar');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showSuccess('Servicio eliminado');
      setDeleteTarget(null);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Error al eliminar');
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Tipos de servicio</h1>
          <p className={pageStyles.subtitle}>
            Servicios activos se muestran en el widget
          </p>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-3 w-3 mr-1" />
          Nuevo
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-100">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setIncludeInactive(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Mostrar inactivos
          </label>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            Recargar
          </Button>
        </div>

        {isError && (
          <div className="m-3 rounded bg-red-50 px-3 py-2 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-red-800">Error al cargar</p>
              <p className="text-xs text-red-700">{(error as Error)?.message}</p>
            </div>
          </div>
        )}

        {!isLoading && !isError && sorted.length === 0 && (
          <div className="text-xs text-gray-500 p-3">No hay tipos de servicio.</div>
        )}

        {!isLoading && !isError && sorted.length > 0 && (
          <div className="divide-y divide-gray-100">
            {sorted.map((st) => (
              <div
                key={st.id}
                className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50/50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900 truncate">{st.name}</span>
                    <span className="text-[10px] rounded-full bg-blue-100 px-1.5 py-0.5 text-blue-700">
                      {BAY_TYPE_LABELS[st.bayType] || 'Mecánica'}
                    </span>
                    {!st.isActive && (
                      <span className="text-[10px] rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-500">
                        Inactivo
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {st.estimatedDurationMins} min · {st.slotsPerHour} cupos/h · Orden {st.sortOrder}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleActive(st)}
                    disabled={updateMutation.isPending}
                    className="text-[10px] px-2 py-1 rounded hover:bg-gray-100 text-gray-600"
                  >
                    {st.isActive ? 'Desactivar' : 'Activar'}
                  </button>
                  <button onClick={() => openEdit(st)} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button onClick={() => setDeleteTarget(st)} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Editar servicio' : 'Nuevo servicio'}
        size="md"
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Nombre *</label>
              <Input
                className="h-8 text-sm"
                value={draft.name}
                onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Chapa y pintura"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Nombre (EN)</label>
              <Input
                className="h-8 text-sm"
                value={draft.nameEn || ''}
                onChange={(e) => setDraft((prev) => ({ ...prev, nameEn: e.target.value }))}
                placeholder="Bodywork and paint"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">Descripción</label>
            <textarea
              value={draft.description || ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              rows={2}
              maxLength={500}
              placeholder="Opcional"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">Tipo de trabajo *</label>
            <select
              value={draft.bayType || 'mechanical'}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, bayType: e.target.value as BayType }))
              }
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm bg-white h-8"
            >
              {BAY_TYPES.map((bt) => (
                <option key={bt} value={bt}>
                  {BAY_TYPE_LABELS[bt]}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Cupos/hora</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={draft.slotsPerHour}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    slotsPerHour: normalizeNumber(e.target.value, 1),
                  }))
                }
                min={1}
                max={10}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Duración (min)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={draft.estimatedDurationMins}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    estimatedDurationMins: normalizeNumber(e.target.value, 60),
                  }))
                }
                min={15}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Intervalo mant. (días)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={draft.maintenanceIntervalDays ?? ''}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    maintenanceIntervalDays: normalizeOptionalNumber(e.target.value),
                  }))
                }
                min={1}
                placeholder="Opcional"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Orden</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={draft.sortOrder}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    sortOrder: normalizeNumber(e.target.value, 0),
                  }))
                }
              />
            </div>
          </div>

          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-3.5 w-3.5"
            />
            Activo (visible en widget)
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              Guardar
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Eliminar tipo de servicio"
        description={`¿Eliminar "${deleteTarget?.name}"? Si tiene registros de historial asociados, no se podrá eliminar.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
}
